import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@center-pwa/store/store';
import {
  emitEvent,
  subscribeToEvent,
  unsubscribeFromEvent,
  MetaspaceEvent,
} from '@center-pwa/reducers/metaspaceEventsReducer';

interface UseMetaspaceEventsOptions {
  subscriberId?: string;
  eventTypes?: string[];
  onEvent?: (event: MetaspaceEvent) => void;
}

export const useMetaspaceEvents = (options: UseMetaspaceEventsOptions = {}) => {
  const dispatch = useDispatch();
  const { lastEvent, events } = useSelector((state: RootState) => state.metaspaceEvents);

  const { subscriberId = 'default', eventTypes = [], onEvent } = options;

  // Subscribe to event types on mount
  useEffect(() => {
    if (eventTypes.length > 0) {
      for (const eventType of eventTypes) {
        dispatch(subscribeToEvent({ eventType, subscriberId }));
      }

      return () => {
        for (const eventType of eventTypes) {
          dispatch(unsubscribeFromEvent({ eventType, subscriberId }));
        }
      };
    }
  }, [dispatch, subscriberId, eventTypes]);

  // Handle incoming events - use a ref to avoid triggering on onEvent changes
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (lastEvent && onEventRef.current && eventTypes.includes(lastEvent.type)) {
      onEventRef.current(lastEvent);
    }
  }, [lastEvent, eventTypes]);

  // Emit event function
  const emit = useCallback(
    (eventType: MetaspaceEvent['type'], payload: Omit<MetaspaceEvent['payload'], 'timestamp'>) => {
      const event = {
        type: eventType,
        payload: {
          ...payload,
          timestamp: Date.now(),
        },
      };
      console.log('📨 [EventSystem] Emitting event:', event);
      dispatch(emitEvent(event));
    },
    [dispatch]
  );

  // Emit specific event types
  const emitChatEvent = useCallback(
    (sphereId: string, additionalData: Record<string, any> = {}) => {
      emit('CHAT_TRIGGERED', {
        sphereId,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitToolbarExpand = useCallback(
    (activeItem: string, sphereId?: string, additionalData: Record<string, any> = {}) => {
      emit('TOOLBAR_EXPAND', {
        sphereId,
        activeItem,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitThreadContextLoad = useCallback(
    (threadId: string, sphereId?: string, additionalData: Record<string, any> = {}) => {
      emit('THREAD_CONTEXT_LOAD', {
        threadId,
        sphereId,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitSphereCreated = useCallback(
    (sphereId: string, parentId?: string, ancestorIds?: string[], additionalData: Record<string, any> = {}) => {
      emit('SPHERE_CREATED', {
        sphereId,
        parentId,
        ancestorIds,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitSphereUpdated = useCallback(
    (sphereId: string, parentId?: string, ancestorIds?: string[], additionalData: Record<string, any> = {}) => {
      emit('SPHERE_UPDATED', {
        sphereId,
        parentId,
        ancestorIds,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitSphereStatusChanged = useCallback(
    (sphereId: string, ancestorIds?: string[], additionalData: Record<string, any> = {}) => {
      emit('SPHERE_STATUS_CHANGED', {
        sphereId,
        ancestorIds,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitOpenSphereEditor = useCallback(
    (sphereId: string, initialTab?: string, additionalData: Record<string, any> = {}) => {
      emit('OPEN_SPHERE_EDITOR', {
        sphereId,
        initialTab,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitTaskFilter = useCallback(
    (status: string | null, sphereId?: string, additionalData: Record<string, any> = {}) => {
      emit('TASK_FILTER_TRIGGERED', {
        status,
        sphereId,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitSphereCentered = useCallback(
    (sphereId: string, sphereType?: string, additionalData: Record<string, any> = {}) => {
      emit('SPHERE_CENTERED', {
        sphereId,
        sphereType,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitTimelineTriggered = useCallback(
    (timelineSphereId: string, additionalData: Record<string, any> = {}) => {
      emit('TIMELINE_TRIGGERED', {
        timelineSphereId,
        ...additionalData,
      });
    },
    [emit]
  );

  const emitConversationMessageClicked = useCallback(
    (messageId: string, threadId: string, contextKey: string, additionalData: Record<string, any> = {}) => {
      const sphereMatch = /sphere:([^|]+)/.exec(contextKey);
      const sphereId = sphereMatch ? sphereMatch[1] : undefined;
      emit('CONVERSATION_MESSAGE_CLICKED', {
        messageId,
        threadId,
        contextKey,
        sphereId,
        ...additionalData,
      });
    },
    [emit]
  );

  return {
    emit,
    emitChatEvent,
    emitToolbarExpand,
    emitThreadContextLoad,
    emitSphereCreated,
    emitSphereUpdated,
    emitSphereStatusChanged,
    emitOpenSphereEditor,
    emitTaskFilter,
    emitSphereCentered,
    emitTimelineTriggered,
    emitConversationMessageClicked,
    lastEvent,
    events,
  };
};